<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
  },
  selectedSession: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const sessionName = ref('');
const qrCode = ref(null);
const sessionStatus = ref('STOPPED');
const isCreatingSession = ref(false);
const isPollingQr = ref(false);
const qrPollingInterval = ref(null);
const sessionId = ref(null);

const isCreateMode = computed(() => props.type === 'create');
const isQrCodeMode = computed(() => props.type === 'qr_code');

const modalTitle = computed(() => {
  if (qrCode.value || isQrCodeMode.value) {
    return t('WAHA_SESSIONS.QR.TITLE');
  }
  return isCreateMode.value
    ? t('WAHA_SESSIONS.ADD.TITLE')
    : t('WAHA_SESSIONS.EDIT.TITLE');
});

const resetForm = () => {
  sessionName.value = '';
  qrCode.value = null;
  sessionStatus.value = 'STOPPED';
  isCreatingSession.value = false;
  isPollingQr.value = false;
  sessionId.value = null;
  if (qrPollingInterval.value) {
    clearInterval(qrPollingInterval.value);
    qrPollingInterval.value = null;
  }
};

const stopQrPolling = () => {
  if (qrPollingInterval.value) {
    clearInterval(qrPollingInterval.value);
    qrPollingInterval.value = null;
  }
  isPollingQr.value = false;
};

const fetchQrCode = async () => {
  try {
    const response = await store.dispatch('wahaSessions/getQrCode', {
      id: sessionId.value,
      format: 'base64',
    });
    qrCode.value = response.qr;
  } catch (error) {
    // Error fetching QR code
  }
};

const checkSessionStatus = async () => {
  try {
    const session = await store.dispatch(
      'wahaSessions/getStatus',
      sessionId.value
    );
    sessionStatus.value = session.status;

    if (session.status === 'WORKING') {
      stopQrPolling();
      useAlert(t('WAHA_SESSIONS.QR.SUCCESS_MESSAGE'));
      await store.dispatch('wahaSessions/get');
      dialogRef.value.close();
    } else if (session.status === 'FAILED') {
      stopQrPolling();
      useAlert(t('WAHA_SESSIONS.QR.FAILED_MESSAGE'));
    }
  } catch (error) {
    // Error checking session status
  }
};

const startQrPolling = () => {
  fetchQrCode();
  qrPollingInterval.value = setInterval(() => {
    fetchQrCode();
    checkSessionStatus();
  }, 5000);
};

const validateSessionName = name => {
  // Only allow alphanumeric, hyphens, and underscores
  const validPattern = /^[a-zA-Z0-9_-]*$/;
  return validPattern.test(name);
};

const handleSessionNameInput = event => {
  const value = event.target.value;
  // Remove invalid characters as user types
  sessionName.value = value.replace(/[^a-zA-Z0-9_-]/g, '');
};

const createSession = async () => {
  if (!sessionName.value.trim()) {
    useAlert(t('WAHA_SESSIONS.ADD.FORM.NAME.ERROR'));
    return;
  }

  if (!validateSessionName(sessionName.value)) {
    useAlert(t('WAHA_SESSIONS.ADD.FORM.NAME.INVALID'));
    return;
  }

  isCreatingSession.value = true;
  try {
    const session = await store.dispatch('wahaSessions/create', {
      name: sessionName.value.trim(),
      config: {},
    });

    sessionId.value = session.id;
    sessionStatus.value = session.status;

    // Start the session automatically
    await store.dispatch('wahaSessions/start', session.id);

    // Start polling for QR code
    isPollingQr.value = true;
    startQrPolling();

    useAlert(t('WAHA_SESSIONS.ADD.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('WAHA_SESSIONS.ADD.API.ERROR_MESSAGE'));
    dialogRef.value.close();
  } finally {
    isCreatingSession.value = false;
  }
};

const onDialogClose = () => {
  stopQrPolling();
  resetForm();
};

watch(
  () => props.selectedSession,
  newValue => {
    if (newValue && newValue.id) {
      sessionName.value = newValue.name || '';
      sessionId.value = newValue.id;

      // If opening in QR code mode, start polling immediately
      if (isQrCodeMode.value && newValue.status === 'SCAN_QR_CODE') {
        isPollingQr.value = true;
        startQrPolling();
      }
    }
  },
  { immediate: true }
);

defineExpose({
  dialogRef,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="modalTitle"
    size="medium"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onDialogClose"
  >
    <div v-if="!qrCode && !isQrCodeMode" class="flex flex-col gap-4">
      <Input
        id="session-name"
        v-model="sessionName"
        :label="t('WAHA_SESSIONS.ADD.FORM.NAME.LABEL')"
        :placeholder="t('WAHA_SESSIONS.ADD.FORM.NAME.PLACEHOLDER')"
        :message="t('WAHA_SESSIONS.ADD.FORM.NAME.HELP')"
        message-type="info"
        :disabled="isCreatingSession"
        @input="handleSessionNameInput"
      />

      <div class="flex gap-2 justify-end">
        <Button
          slate
          :label="t('WAHA_SESSIONS.FORM.CANCEL')"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WAHA_SESSIONS.ADD.FORM.SUBMIT')"
          :is-loading="isCreatingSession"
          @click="createSession"
        />
      </div>
    </div>

    <div v-else class="flex flex-col items-center gap-4">
      <div class="flex flex-col items-center gap-2">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{ t('WAHA_SESSIONS.QR.SCAN_INSTRUCTIONS') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center">
          {{ t('WAHA_SESSIONS.QR.HELP_TEXT') }}
        </p>
      </div>

      <div class="p-4 bg-white rounded-lg border border-n-weak">
        <img
          v-if="qrCode"
          :src="qrCode"
          alt="WhatsApp QR Code"
          class="w-64 h-64"
        />
        <div v-else class="w-64 h-64 flex items-center justify-center">
          <span class="i-lucide-loader animate-spin w-8 h-8 text-n-slate-11" />
        </div>
      </div>

      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span class="i-lucide-loader animate-spin w-4 h-4" />
        <span>{{ t('WAHA_SESSIONS.QR.WAITING_FOR_SCAN') }}</span>
      </div>

      <div
        v-if="sessionStatus === 'SCAN_QR_CODE'"
        class="flex flex-col items-center gap-2 p-4 bg-n-yellow-3 border border-n-yellow-6 rounded-lg w-full"
      >
        <span class="text-sm font-medium text-n-yellow-11">
          {{ t('WAHA_SESSIONS.QR.STATUS.SCAN_QR_CODE') }}
        </span>
      </div>

      <div class="flex gap-2 justify-end w-full">
        <Button
          slate
          :label="t('WAHA_SESSIONS.QR.CANCEL')"
          @click="dialogRef.close()"
        />
      </div>
    </div>
  </Dialog>
</template>
